import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { Alert, App, Button } from 'antd'
import type { ServiceOrder } from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { OrderBillsDrawer, refreshMoneyQueries } from '@/features/bill/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export function ServiceInitialBillPanel({
  order,
  children
}: {
  order: ServiceOrder
  children: (fees: ReactNode, actions: ReactNode) => ReactNode
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const canRead = currentSession.isSuperAdmin || currentSession.permissions.includes('bill.read')
  const canManage = currentSession.isSuperAdmin || currentSession.permissions.includes('bill.generate')
  const [open, setOpen] = useState(false)
  const generate = useMutation(trpc.bill.initialServiceBill.mutationOptions())
  const run = async () => {
    try {
      await generate.mutateAsync({ serviceOrderId: order.id })
      await refreshMoneyQueries(cache, trpc)
      void message.success('服务费账单已就绪')
      if (canRead) setOpen(true)
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '生成账单失败')
    }
  }
  const billLabels = {
    draft: '服务费草稿已生成，待核费出账',
    issued: '服务费已出账，请在账单中核对收款',
    voided: '原服务费账单已作废，历史记录保留'
  }
  const feeTitle = order.initialBillStatus ? billLabels[order.initialBillStatus] : '尚未生成服务费草稿'
  const fees = (
    <Alert
      showIcon
      type="info"
      title={feeTitle}
      description="一次性服务在创建订单时生成全额费用草稿。“待办理”表示服务尚未完成，不代表没有账单；费用草稿须核费出账后再收款。"
    />
  )
  const actions = (
    <>
      {canRead && <Button onClick={() => setOpen(true)}>查看账单与收款</Button>}
      {canManage &&
        !order.initialBillStatus &&
        !order.archivedAt &&
        ['pending', 'completed'].includes(order.status) && (
          <Authorized permissions={['bill.generate']}>
            <Button loading={generate.isPending} onClick={() => void run()}>
              补建服务费草稿
            </Button>
          </Authorized>
        )}
    </>
  )
  return (
    <>
      {children(fees, actions)}
      {open && (
        <OrderBillsDrawer
          order={{ type: 'service', id: order.id, orderNo: order.orderNo }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}
